import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { getIframelyData } from '#src/app/shared/api/medias';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import type { MediaHitComment, MediaHitEmbed, MediaHitsContextValue } from './types';

const toLoadingEmbed = ({ id, url, mediaSource }: MediaHitComment): MediaHitEmbed => ({
	id,
	url,
	mediaSource,
	status: 'loading',
	html: '',
});

// oxlint-disable-next-line max-lines-per-function
export function useMediaHitsContextValue(): MediaHitsContextValue {
	const [mediaEmbeds, setMediaEmbeds] = useState<MediaHitEmbed[]>([]);
	const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | undefined>();
	// Tracks which `serverMediaHits` set we've already started embed fetches for, keyed by reference (the page-data
	// array is stable per petition). Keyed rather than a boolean so a new dataset is always (re)loaded — including a
	// client-side navigation to another petition where this provider stays mounted. A boolean guard reset in the
	// seeding effect would be reset too late: the consuming section's effect runs (child-first) before this one.
	const loadedFor = useRef<unknown>(null);

	const {
		data: {
			petition: { id: petitionId },
			mediaHitsPage: { mediaHits: serverMediaHits },
		},
	} = usePetitionDetailsPageData();

	const utilityContext = useUtilityContext();
	const { track } = useCampaignTracking();

	// Seed the embeds (in a loading status) from the server data. Embeds are not fetched here — that is deferred to
	// `loadEmbeds`, which the section triggers once it nears the viewport. On a dataset change (client-side navigation
	// to another petition, or a page-data refresh) this reseeds to `loading`; `loadEmbeds` re-loads on its own because
	// its guard is keyed to the dataset (see `loadedFor`).
	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setMediaEmbeds(serverMediaHits.map(toLoadingEmbed));
	}, [serverMediaHits]);

	const resolveEmbed = useCallback(
		async ({ id, url }: { id: string; url: string }) => {
			try {
				const { html } = await getIframelyData({ url, format: 'SMALL' }, utilityContext);
				setMediaEmbeds((curState) =>
					curState.map((embed) => (embed.id === id ? { ...embed, status: 'valid', html } : embed)),
				);
			} catch {
				// Do not throw when iframely fails to generate an embed, mark the single embed invalid and let the rest of the list resolve independently.
				setMediaEmbeds((curState) =>
					curState.map((embed) => (embed.id === id ? { ...embed, status: 'invalid', html: '' } : embed)),
				);
				track('media_hit_iframely_embed_failure', { petition_id: petitionId, media_url: url });
			}
		},
		[utilityContext, track, petitionId],
	);

	const loadEmbeds = useCallback(() => {
		// Skip only if we've already started loading *this* dataset; a new `serverMediaHits` always (re)loads.
		if (loadedFor.current === serverMediaHits) return;
		loadedFor.current = serverMediaHits;
		// Fire each embed fetch independently (no `Promise.all` gating) so the list isn't held on the slowest one.
		serverMediaHits.forEach((mediaHit) => void resolveEmbed(mediaHit));
	}, [serverMediaHits, resolveEmbed]);

	const removeMedia = useCallback((mediaId: string) => {
		setMediaEmbeds((curState) => curState.filter((embed) => embed.id !== mediaId));
	}, []);

	const addMedia = useCallback(
		(id: string, url: string) => {
			setMediaEmbeds((curState) => [toLoadingEmbed({ id, url, mediaSource: 'STAFF_ADDED' }), ...curState]);
			void resolveEmbed({ id, url });
		},
		[resolveEmbed],
	);

	return useMemo(
		() => ({
			data: {
				mediaEmbeds,
				submitStatus,
			},
			actions: {
				loadEmbeds,
				addMedia,
				removeMedia,
				setSubmitStatus,
			},
		}),
		[mediaEmbeds, submitStatus, loadEmbeds, addMedia, removeMedia, setSubmitStatus],
	);
}
